import { CreationType } from '../../../app-shared-elements/_enums/registrator-sync-status.enu';
import { Device } from '../../../app-shared-elements/_interfaces/Device';
import { Variable } from '../../../app-shared-elements/_interfaces/Variable';

export function getMnemonicDevices(devices: Device[] | null | undefined): Device[] {
    return (devices ?? []).filter((device) => device.creationType === CreationType.MNEMONIC);
}

export function findMnemonicBoundVariable(
    mnemonicDevices: Device[],
    variableId?: string,
    preferredMnemonicDeviceId?: string,
): Variable | undefined {
    if (!variableId || !mnemonicDevices.length) {
        return undefined;
    }

    if (preferredMnemonicDeviceId) {
        const preferredDevice = mnemonicDevices.find((device) => device.id === preferredMnemonicDeviceId);
        const preferredVariable = findDeviceVariable(preferredDevice, variableId);

        if (preferredVariable) {
            return preferredVariable;
        }
    }

    for (const device of mnemonicDevices) {
        const variable = findDeviceVariable(device, variableId);

        if (variable) {
            return variable;
        }
    }

    return undefined;
}

export function formatMnemonicBindingLabel(variableName: string, deviceName?: string): string {
    const variable = variableName?.trim() ?? '';
    const device = deviceName?.trim() ?? '';

    if (!variable) {
        return device;
    }

    if (!device) {
        return variable;
    }

    return `${variable} (${device})`;
}

export function resolveDeviceDisplayName(device?: Pick<Device, 'name' | 'defaultName'> | null): string {
    return device?.name?.trim() || device?.defaultName?.trim() || '';
}

export function resolveVariableDisplayName(variable?: Pick<Variable, 'customName' | 'name'> | null): string {
    const customName = variable?.customName?.trim();

    if (customName) {
        return customName;
    }

    return variable?.name?.trim() ?? '';
}

export function resolveBoundVariableDisplayName(
    mnemonicVariable?: Pick<Variable, 'customName' | 'name' | 'originVariableId' | 'id'> | null,
    originDevice?: Device,
    variableId?: string,
): string {
    const resolvedVariableId = variableId || mnemonicVariable?.originVariableId || mnemonicVariable?.id;
    const originVariable = findDeviceVariable(originDevice, resolvedVariableId);
    const customName = originVariable?.customName?.trim() || mnemonicVariable?.customName?.trim();

    if (customName) {
        return customName;
    }

    return originVariable?.name?.trim() || mnemonicVariable?.name?.trim() || '';
}

export function findDeviceVariable(device: Device | undefined, variableId?: string): Variable | undefined {
    if (!device || !variableId) {
        return undefined;
    }

    return device.variables?.find((item) => item.id === variableId || item.originVariableId === variableId);
}

export function resolveRuntimeBoundVariable(
    allDevices: Device[],
    mnemonicDevices: Device[],
    params: { variableId?: string; deviceId?: string; preferredMnemonicDeviceId?: string },
): Variable | undefined {
    const mnemonicVariable = findMnemonicBoundVariable(
        mnemonicDevices,
        params.variableId,
        params.preferredMnemonicDeviceId,
    );

    if (mnemonicVariable) {
        return mnemonicVariable;
    }

    const originDevice = allDevices.find((device) => device.id === params.deviceId);

    return findDeviceVariable(originDevice, params.variableId);
}

export function resolveMnemonicBindingLabel(
    object: { name?: string; variableId?: string; deviceId?: string },
    devices?: Device[],
    mnemonicDevices?: Device[],
    preferredMnemonicDeviceId?: string,
): string {
    if (!object.variableId) {
        const objectName = object.name?.trim() ?? '';

        if (objectName && !/^chart\d+$/i.test(objectName)) {
            return objectName;
        }

        return '';
    }

    const originDevice = devices?.find((item) => item.id === object.deviceId);
    const mnemonicVariable = mnemonicDevices?.length
        ? findMnemonicBoundVariable(mnemonicDevices, object.variableId, preferredMnemonicDeviceId)
        : findDeviceVariable(originDevice, object.variableId);
    const variableName = resolveBoundVariableDisplayName(mnemonicVariable, originDevice, object.variableId);
    const deviceName = resolveDeviceDisplayName(originDevice);

    if (variableName) {
        return formatMnemonicBindingLabel(variableName, deviceName);
    }

    return '';
}
